import React from 'react';
import { motion } from 'framer-motion';
import { Package } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { SectionHeader } from './shared/SectionHeader';
import { TrendBadge } from './shared/TrendBadge';
import { SkeletonLoader } from './shared/SkeletonLoader';
import { formatCurrencyCompact, formatStat } from '../utils/formatters';

const DONUT_COLORS = ['#3B82F6', '#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316'];

export function EcommerceAnalytics({
  topProducts, categoryPerformance, dailyProductData,
  isMarketer, isLoading,
}) {
  if (isMarketer) return null;

  const products = (topProducts || []).slice(0, 10);
  const categories = (categoryPerformance || []).slice(0, 6);
  const changes = dailyProductData?.changes || {};
  const totals = dailyProductData?.totals || {};

  const hasData = products.length > 0 || categories.length > 0;
  if (!hasData && !isLoading) return null;

  if (isLoading) return <SkeletonLoader variant="table" rows={6} />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <SectionHeader title="E-commerce Analytics" icon={Package} />

      {/* KPI Cards */}
      {totals && Object.keys(totals).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Revenue', value: formatCurrencyCompact(totals.revenue), trend: changes.revenue },
            { label: 'Transactions', value: formatStat(totals.transactions), trend: changes.transactions },
            { label: 'Sessions', value: formatStat(totals.sessions), trend: changes.sessions },
            { label: 'Users', value: formatStat(totals.users), trend: changes.users },
          ].map((kpi) => (
            <div key={kpi.label} className="glass-card rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{kpi.label}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-lg font-bold text-gray-900">{kpi.value}</span>
                {kpi.trend != null && <TrendBadge value={kpi.trend} size="sm" />}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        {products.length > 0 && (
          <div className="glass-card rounded-2xl p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Top Products</h3>
            <div className="space-y-2.5">
              {products.map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-[10px] font-bold text-blue-600 shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{p.itemName}</p>
                    {p.itemBrand && <p className="text-[10px] text-gray-400 truncate">{p.itemBrand}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-bold text-gray-900">{formatCurrencyCompact(p.totalRevenue)}</p>
                    <p className="text-[10px] text-gray-400">{p.totalItemsPurchased} sold</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category Performance */}
        {categories.length > 0 && (
          <div className="glass-card rounded-2xl p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Category Performance</h3>
            <div className="flex items-center gap-4">
              <div style={{ width: 140, height: 140 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={categories.map(c => ({ name: c.itemCategory, value: c.totalRevenue }))}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                    >
                      {categories.map((_, i) => (
                        <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                {categories.map((c, i) => {
                  const shortName = (c.itemCategory || '').split('>').pop().trim() || c.itemCategory;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                      <span className="text-[11px] text-gray-600 truncate flex-1">{shortName}</span>
                      <span className="text-[11px] font-semibold text-gray-700">{formatCurrencyCompact(c.totalRevenue)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
